import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 35000
});

apiClient.interceptors.request.use(
  (config) => {
    let token;
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        token = parsedUser?.token || '';
      } catch (error) {
        console.error('Error parsing user token:', error);
      }
    }

    if (token && config.headers) {
      config.headers['AuthorizationToken'] = `${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  response => response,
  (error) => Promise.reject(error)
);

class WorkoutService {
  /**
   * Get pending payments for workouts
   */
  async getPendingPayments() {
    try {
      const response = await apiClient.get('/workouts/pending-payments');
      return response.data;
    } catch (error) {
      console.error('Error fetching pending payments:', error);
      throw error;
    }
  }

  /**
   * Get pending workouts for competition (paginated)
   */
  async getPendingWorkouts({
    skip = 0,
    limit = 20,
    keyword = '',
    filterBy = 'title',
    status = 'all',
    dateStart = '',
    dateEnd = '',
  } = {}) {
    try {
      const { data } = await apiClient.get('/workouts/pending-workouts', {
        params: {
          skip: String(skip),
          limit: String(limit),
          ...(keyword ? { keyword, filterBy } : {}),
          ...(status && status !== 'all' ? { status } : {}),
          ...(dateStart ? { dateStart } : {}),
          ...(dateEnd ? { dateEnd } : {}),
        },
      });

      if (Array.isArray(data)) {
        return { items: data, total: data.length };
      }

      return {
        items: Array.isArray(data?.items) ? data.items : [],
        total: Number(data?.total) || 0,
      };
    } catch (error) {
      console.error('Error fetching pending workouts:', error);
      throw error;
    }
  }

  /**
   * Approve a workout (BE uses GET route)
   */
  async approveWorkout(workoutId) {
    try {
      const { data } = await apiClient.get(`/workouts/approve/${workoutId}`);
      return data;
    } catch (error) {
      console.error('Error approving workout:', error);
      throw error;
    }
  }

  /**
   * Decline a workout (BE uses GET route)
   */
  async declineWorkout(workoutId) {
    try {
      const { data } = await apiClient.get(`/workouts/decline/${workoutId}`);
      return data;
    } catch (error) {
      console.error('Error declining workout:', error);
      throw error;
    }
  }

  /**
   * Remove a workout (BE uses GET route)
   */
  async removeWorkout(workoutId) {
    try {
      const { data } = await apiClient.get(`/workouts/remove/${workoutId}`);
      return data;
    } catch (error) {
      console.error('Error declining workout:', error);
      throw error;
    }
  }

  /**
   * Process payout for seller
   */
  async payoutSeller(sellerId, notes = '') {
    try {
      const response = await apiClient.post('/workouts/payout_seller', {
        seller_id: sellerId,
        notes,
      });
      return response.data;
    } catch (error) {
      console.error('Error processing seller payout:', error);
      throw error;
    }
  }

  /**
   * Get payout history for a specific trainer
   */
  async getPayoutHistory(trainerId) {
    try {
      const response = await apiClient.get(`/workouts/payout-history/${trainerId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching payout history:', error);
      throw error;
    }
  }

  /**
   * Get payout details by payout record ID
   */
  async getPayoutDetails(payoutId) {
    try {
      const response = await apiClient.get(`/workouts/payout-details/${payoutId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching payout details:', error);
      throw error;
    }
  }

  /**
   * Get all payout history (admin overview)
   */
  async getAllPayoutHistory() {
    try {
      const response = await apiClient.get('/workouts/payout-history-all');
      return response.data;
    } catch (error) {
      console.error('Error fetching all payout history:', error);
      throw error;
    }
  }

  /**
   * Get referral earnings for a specific trainer
   */
  async getReferralEarnings(trainerId) {
    try {
      const response = await apiClient.get(`/workouts/referral-earnings/${trainerId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching referral earnings:', error);
      throw error;
    }
  }

  /**
   * Update income percentage for seller
   */
  async updateIncomePercentage(sellerId, percentage) {
    try {
      const response = await apiClient.put('/workouts/update_income_percentage', {
        sellerId,
        percentage
      });
      return response.data;
    } catch (error) {
      console.error('Error updating income percentage:', error);
      throw error;
    }
  }

  /**
   * Update referral commission percentage for a user (referrer).
   * Controls how much of the athlete's share goes to their referrer.
   */
  async updateReferralCommission(userId, referralCommissionPercent) {
    try {
      const response = await apiClient.put('/workouts/update_referral_commission', {
        userId,
        referralCommissionPercent
      });
      return response.data;
    } catch (error) {
      console.error('Error updating referral commission:', error);
      throw error;
    }
  }

  /**
   * Update referral period (start/end dates) for a referred user.
   * Dates should be ISO date strings (YYYY-MM-DD) or null to clear.
   */
  async updateReferralPeriod(userId, referralStartDate, referralEndDate) {
    try {
      const body = { userId };
      if (referralStartDate !== undefined) {
        body.referralStartDate = referralStartDate;
      }
      if (referralEndDate !== undefined) {
        body.referralEndDate = referralEndDate;
      }
      const response = await apiClient.put('/workouts/update_referral_period', body);
      return response.data;
    } catch (error) {
      console.error('Error updating referral period:', error);
      throw error;
    }
  }

  /**
   * Admin view of all referral relationships across the platform.
   */
  async getAdminReferrals() {
    try {
      const { data } = await apiClient.get('/workouts/referrals-admin');
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching admin referrals:', error);
      throw error;
    }
  }
}

export default new WorkoutService();